import { format, startOfMonth, startOfYear, subDays } from 'date-fns';
import styled from 'styled-components';

import { Chip } from 'components/shared/FilterChips';

const today = new Date();

export type DatePickerShortcutItem<D extends Date | null = Date> = { label: string; value: [D, D] };

const shortcutItems: DatePickerShortcutItem[] = [
  { label: 'This Month', value: [startOfMonth(today), today] },
  { label: 'Last 30 Days', value: [subDays(today, 30), today] },
  { label: 'Last 90 Days', value: [subDays(today, 90), today] },
  { label: 'Year To Date', value: [startOfYear(today), today] },
];

type DatePickerShortcutsProps<D extends Date | null> = {
  datePickerRef: any;
  setDates: (dates: [D, D]) => void;
  startDate: Date | null;
  endDate: Date | null;
  // An open-ended preset (`null` on one side) is only possible for callers that opt in by
  // widening D.
  items?: DatePickerShortcutItem<D>[];
};

const DatePickerShortcuts = <D extends Date | null = Date>({
  datePickerRef,
  setDates,
  startDate,
  endDate,
  items = shortcutItems as DatePickerShortcutItem<D>[],
}: DatePickerShortcutsProps<D>) => {
  const formatString = 'MM-dd-yyyy';
  const formatOrBlank = (date: Date | null) => (date ? format(date, formatString) : '');

  const formattedStartDate = formatOrBlank(startDate);
  const formattedEndDate = formatOrBlank(endDate);

  return (
    <StyledChipBag>
      {items.map((item) => {
        const formattedItemStartDate = formatOrBlank(item.value[0]);
        const formattedItemEndDate = formatOrBlank(item.value[1]);

        return (
          <StyledChip
            $isHighlighted={formattedStartDate === formattedItemStartDate && formattedEndDate === formattedItemEndDate}
            key={item.label}
            onClick={() => {
              setDates(item.value);
              datePickerRef.current.setOpen(false);
            }}>
            {item.label}
          </StyledChip>
        );
      })}
    </StyledChipBag>
  );
};

export default DatePickerShortcuts;

const StyledChip = styled(Chip)<{ $isHighlighted: boolean }>`
  cursor: pointer;
  font-weight: normal;

  ${({ $isHighlighted, theme }) => $isHighlighted && `background-color: ${theme.colors.blue100}; color: white;`}
`;

const StyledChipBag = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
`;
